import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

const EMPTY = { active: false, paths: null, signedIn: false, settings: null };

/**
 * Cloud Backup state for the Library's per-row marker: `active` = the files
 * backup layer is on for this owner; `paths` = Set of local file paths whose
 * bytes are stored. The Library marks the EXCEPTIONS (active && not stored) —
 * backed-up rows stay clean, iCloud-style. One cheap IPC pair (local
 * backup_items SELECT, no network) on mount/account-switch, refreshed when
 * the files engine's progress stream reports a run finishing. Inactive on
 * web/native (no backup bridge) and while signed out.
 *
 * Also carries `signedIn` + the owner's raw `settings`, so the Library can
 * tell "backup is off for this account" (its one-line notice,
 * shouldShowLibraryBackupLine) apart from "no bridge / signed out" — both
 * are `active: false`.
 */
export default function useBackedUpPaths() {
    const backupApi = typeof window !== 'undefined' ? window.electron?.backup : null;
    const userId = useSelector((s) => s.auth?.user?.id) || null;
    const [state, setState] = useState(EMPTY);

    useEffect(() => {
        if (!backupApi?.storedFilePaths || !userId) {
            setState(EMPTY);
            return undefined;
        }
        let cancelled = false;
        const refresh = () => {
            Promise.all([backupApi.getStatus(), backupApi.storedFilePaths()]).then(([status, stored]) => {
                if (cancelled) return;
                const ok = !!(status && !status.error);
                const active = !!(ok && status.signedIn
                    && status.settings?.enabled && status.settings?.files === true);
                setState({
                    active,
                    paths: new Set((stored && !stored.error && stored.paths) || []),
                    signedIn: !!(ok && status.signedIn),
                    settings: ok ? (status.settings || null) : null,
                });
            }).catch(() => {});
        };
        refresh();
        // A files event with no currentFile is the run going quiet — the
        // moment newly-stored rows exist. While a run IS active, refresh on
        // a throttle so the Library's cloud column flips rows to "backed up"
        // live as uploads land (the IPC is a local SELECT — cheap). Snapshot
        // phases don't touch files.
        let lastLiveRefresh = 0;
        const off = backupApi.onProgress?.((p) => {
            // A whole pass ending is the settings having possibly changed
            // (enable from the Backup page → snapshots → files): re-read so
            // the off-notice yields to the column without a remount.
            if (p?.phase === 'pass' && p.event === 'end') { refresh(); return; }
            if (p?.phase !== 'files') return;
            if (!p.currentFile) { refresh(); return; }
            const now = Date.now();
            if (now - lastLiveRefresh > 4000) { lastLiveRefresh = now; refresh(); }
        });
        return () => { cancelled = true; if (off) off(); };
    }, [backupApi, userId]);

    return state;
}
